// Authenticated app frame: fixed dark sidebar, one concern per screen.
// The sidebar is the product's spine: exam switcher on top, then the study
// loop in order (dashboard, lessons, practice, mocks, progress, submissions).
// Exam runner pages do NOT use this shell, the exam takes the whole screen.
import React, { useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { startTour } from '@/components/QuickTour';
import {
  BarChart3,
  BookOpen,
  Building2,
  ClipboardList,
  Compass,
  Inbox,
  LayoutGrid,
  LogOut,
  Target,
  Timer,
  Users,
} from 'lucide-react';

import { useApp } from '@/lib/store';
import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/utils';
import DemoPersonaSwitch from '@/components/DemoPersonaSwitch';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import LangSwitcher from '@/components/LangSwitcher';
import { tourNavLabel } from '@/lib/tourI18n';

const EXAM_NAMES = { celpip: 'CELPIP', pte: 'PTE Core' };

export default function AppShell({ children }) {
  const { user, signOut, activeExam: prefExam, setActiveExam, effectiveRole, effectiveProvisionedBy, lang } = useApp();
  const { exam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeExam = exam || prefExam || user?.exams?.[0] || 'celpip';

  // When you're on an exam-scoped page, the URL is the source of truth, remember it
  // so the dashboard stays on the same exam afterward.
  useEffect(() => { if (exam && exam !== prefExam) setActiveExam(exam); }, [exam, prefExam, setActiveExam]);

  const switchExamPath = (target) => {
    // Don't blind-swap the exam slug: task-type and unit ids are exam-specific,
    // so carrying them across exams lands on a broken page. Keep a bare top-level
    // area; otherwise drop to that area's hub for the target exam.
    if (!exam) return `/${target}/`;
    const seg = location.pathname.split('/').filter(Boolean); // [exam, area, id?]
    const area = seg[1];
    if (['lessons', 'walkthrough'].includes(area)) return `/${target}/lessons`;
    if (['practice', 'sets', 'models', 'write', 'speak'].includes(area)) return `/${target}/practice`;
    if (['mocks', 'mock'].includes(area)) return `/${target}/mocks`;
    if (['results', 'progress', 'diagnostic'].includes(area)) return `/${target}/${area}`;
    return `/${target}/`;
  };

  const nav = [
    { to: '/', label: 'Dashboard', icon: LayoutGrid, end: true },
    { to: `/${activeExam}/diagnostic`, label: 'Diagnostic', icon: Compass },
    { to: `/${activeExam}/lessons`, label: 'Lessons', icon: BookOpen },
    { to: `/${activeExam}/practice`, label: 'Practice', icon: Timer },
    ...(effectiveRole === 'student' ? [{ to: '/assignments', label: 'My assignments', icon: ClipboardList }] : []),
    { to: `/${activeExam}/mocks`, label: 'Mock tests', icon: Target },
    { to: `/${activeExam}/results`, label: 'Results', icon: Inbox },
    { to: `/${activeExam}/progress`, label: 'Progress', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-slate-950 md:flex">
        <Link to="/" data-testid="shell-logo" className="px-6 pb-5 pt-7">
          <span className="h-display text-lg leading-none text-white">
            {BRAND}<span className="text-indigo-400">.</span>
          </span>
        </Link>

        {(user?.exams?.length || 0) > 1 && (
          <div className="mx-4 mb-4 grid grid-cols-2 border border-slate-800" data-testid="exam-switcher">
            {user.exams.map((e) => (
              <button
                key={e}
                type="button"
                data-testid={`switch-exam-${e}`}
                onClick={() => { setActiveExam(e); if (exam && e !== exam) navigate(switchExamPath(e)); }}
                className={cn(
                  'px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide transition-colors',
                  e === activeExam ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                )}
              >
                {EXAM_NAMES[e] || e}
              </button>
            ))}
          </div>
        )}

        <nav className="flex-1 space-y-0.5 px-3">
          {nav.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-l-2 border-indigo-400 bg-slate-900 text-white'
                    : 'border-l-2 border-transparent text-slate-400 hover:bg-slate-900/60 hover:text-white'
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {effectiveRole === 'platform_admin' && (
          <div className="border-t border-slate-800 px-3 py-3">
            <Link
              to="/admin"
              className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-400 hover:text-white"
              data-testid="nav-admin"
            >
              Platform admin
            </Link>
          </div>
        )}

        {effectiveRole === 'org_admin' && (
          <div className="border-t border-slate-800 px-3 py-3">
            <NavLink
              to="/org"
              data-testid="nav-center-admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-l-2 border-indigo-400 bg-slate-900 text-white'
                    : 'border-l-2 border-transparent text-slate-400 hover:bg-slate-900/60 hover:text-white'
                )
              }
            >
              <Building2 className="h-4 w-4 shrink-0" /> Center admin
            </NavLink>
          </div>
        )}

        {effectiveRole === 'co_admin' && (
          <div className="border-t border-slate-800 px-3 py-3">
            <NavLink
              to="/org"
              data-testid="nav-center-admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-l-2 border-indigo-400 bg-slate-900 text-white'
                    : 'border-l-2 border-transparent text-slate-400 hover:bg-slate-900/60 hover:text-white'
                )
              }
            >
              <Building2 className="h-4 w-4 shrink-0" /> Center admin
            </NavLink>
          </div>
        )}

        {effectiveRole === 'instructor' && (
          <div className="border-t border-slate-800 px-3 py-3">
            <NavLink
              to="/org"
              data-testid="nav-teacher-admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-l-2 border-indigo-400 bg-slate-900 text-white'
                    : 'border-l-2 border-transparent text-slate-400 hover:bg-slate-900/60 hover:text-white'
                )
              }
            >
              <Users className="h-4 w-4 shrink-0" /> Teacher Admin
            </NavLink>
          </div>
        )}

        <button
          type="button"
          onClick={startTour}
          data-testid="open-tour"
          className="flex w-full items-center gap-3 border-t border-slate-800 px-6 py-3 text-sm font-medium text-slate-400 transition-colors hover:text-white"
        >
          <Compass className="h-4 w-4" /> {tourNavLabel(lang, 'Take a tour')}
        </button>

        {/* Learning language sits with the account cluster at the bottom of the
            sidebar — it's a preference, not a page. Opens upward so the menu
            doesn't clip below the viewport, and sits above the "Provisioned
            by" badge + user info so the account row stays the visual anchor. */}
        <div className="border-t border-slate-800 px-4 py-3">
          <LangSwitcher direction="up" compact />
        </div>

        {effectiveProvisionedBy && (
          <div className="border-t border-slate-800 px-6 py-3" data-testid="provisioned-by-badge">
            <p className="text-[10px] uppercase tracking-[0.14em] text-slate-600">Provisioned by</p>
            <p className="mt-0.5 truncate text-xs font-medium text-slate-400">{effectiveProvisionedBy}</p>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-800 px-6 py-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="mono-stat truncate !text-slate-500">{EXAM_NAMES[activeExam]}</p>
          </div>
          <button
            type="button"
            onClick={signOut}
            data-testid="nav-signout"
            className="p-1.5 text-slate-500 transition-colors hover:text-white"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      {/* small screens: slim top bar with the same links */}
      <header className="sticky top-0 z-30 flex items-center gap-4 overflow-x-auto border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <Link to="/" className="h-display shrink-0 text-base">{BRAND}<span className="text-indigo-600">.</span></Link>
        {nav.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn('shrink-0 text-sm font-medium', isActive ? 'text-slate-900' : 'text-slate-400')
            }
          >
            {item.label}
          </NavLink>
        ))}
        <button type="button" onClick={signOut} className="ml-auto shrink-0 p-1 text-slate-400">
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="md:pl-60">
        <ImpersonationBanner />
        <div className="mx-auto max-w-5xl px-6 py-10 lg:px-10">{children}</div>
      </main>

      <DemoPersonaSwitch />
    </div>
  );
}
